import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQuery } from '@tanstack/react-query';
import { arrayToObject, getBluecoinData, getBluecoinDataFilter } from './BluecoinService';
import BluecoinForm from './BluecoinForm';
import FilterBluecoinsForm from './FilterBluecoinsForm';
import { BluecoinData, BluecoinsData, FiltersBluecoin } from './types';
import {
  activeFilterBluecoinsAtom,
  // bluecoinIdAtom,
  bluecoinsAtom,
  isBluecoinDataLoadingAtom,
  isMinimalizedBleAtom,
} from './BluecoinState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IBluecoinsProps {
  map: google.maps.Map | undefined;
}

export default function Bluecoins(props: IBluecoinsProps) {
  const { formatMessage: f } = useIntl();
  const [isOpenedFilter, setIsOpenedFilter] = React.useState(false);
  // const [isOpenedDetail, setIsOpenedDetail] = React.useState(false);
  const setBluecoins = useSetRecoilState<BluecoinsData>(bluecoinsAtom);
  const isBluecoinDataLoading = useSetRecoilState<boolean>(isBluecoinDataLoadingAtom);
  const activeFilterBluecoin = useRecoilValue<FiltersBluecoin | null>(activeFilterBluecoinsAtom);
  // const bluecoinId = useRecoilValue<number | null>(bluecoinIdAtom);
  const menuRef = React.useRef<HTMLDivElement | undefined>();
  const [isMinimalizedBle, setIsMinimalizedBle] = useRecoilState<boolean>(isMinimalizedBleAtom);
  const { trackEvent } = useAnalytics();

  // const handleFilterOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  //   event.preventDefault();
  //   setIsOpenedFilter(true);
  // };

  const handleCloseFilterOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenedFilter(false);
  };

  const handleSaveFilter = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault();
    setIsOpenedFilter(false);
  };

  // const handleOpenDetail = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  //   event.preventDefault();
  //
  //   if (typeof bluecoinId === 'number') {
  //     setIsOpenedDetail(true);
  //   }
  // };

  // const handleCloseDetail = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
  //   if (event) event.preventDefault();
  //   setIsOpenedDetail(false);
  // };

  const getData = async (): Promise<null | BluecoinData[]> => {
    isBluecoinDataLoading(true);
    const response = activeFilterBluecoin
      ? await getBluecoinDataFilter(activeFilterBluecoin)
      : await getBluecoinData();

    if (response) {
      setBluecoins(arrayToObject(response));
      isBluecoinDataLoading(false);
      return response;
    }
    isBluecoinDataLoading(false);
    return null;
  };

  const { isLoading, isFetched } = useQuery({
    queryKey: ['bluecoinsData', activeFilterBluecoin || 'no-filter'],
    queryFn: () => getData(),
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  });

  const handleMenuRef = (element: HTMLDivElement): void => {
    menuRef.current = element;
  };

  const handleOnMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event?.stopPropagation();
    if (menuRef.current) {
      menuRef.current.style.zIndex = '5';
    }
  };

  const handleOnMouseOut = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event?.stopPropagation();
    if (menuRef.current) {
      menuRef.current.style.zIndex = '2';
    }
  };

  const handleMinimalizeBle = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.preventDefault();
    trackEvent(['menu', 'click', 'Minimalize Bluecoins list']);
    setIsMinimalizedBle(true);
  };

  if (isMinimalizedBle) {
    return null;
  }

  return (
    <div
      ref={handleMenuRef}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseOut}
      className="w-mc-waypoints d-none d-lg-block"
      style={{ bottom: '120px', zIndex: '2' }}
    >
      <div className="b-mc-waypoints-tabs">
        <span className="item active">{f({ id: 'menu.bluecoins' })}</span>
        <button
          type="button"
          title={f({ id: 'waypoints.tab.minimalize' })}
          className="item item--collapse"
          onClick={handleMinimalizeBle}
        >
          <i className="cmd-icons cmd-icon-dropdown" />
          <span className="sr-only">{f({ id: 'waypoints.tab.minimalize' })}</span>
        </button>
      </div>
      {!isOpenedFilter && (
        <BluecoinForm map={props.map} isLoading={isLoading} isFetched={isFetched} />
      )}
      {isOpenedFilter && (
        <FilterBluecoinsForm
          closeFilterOptions={handleCloseFilterOptions}
          saveFilterWaypoint={handleSaveFilter}
        />
      )}
    </div>
  );
}
